import * as THREE from "three";
import { MathUtils, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { MLayer } from "@/core/MLayer";
import {
  DBEntity,
  DbLayer,
  DbLine,
  FMathUtils,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { BPMathUtils } from "@/utils/BPMathUtils";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "../IDrawOperation";
import { BPLinear } from "@/core/BPLinear";
import { Context } from "@/core/context";
import { LineDimension } from "@/Dimensions/LineDimension";
import { ArcDimension } from "@/Dimensions/ArcDimension";
import { AnchorPointAux } from "@/Dimensions/AnchorPointAuxs";
import { OrthogonalAux } from "@/Dimensions/OrthogonalAux";
import { ProtectRadius } from "./ProtectRadius";
import { ActiveInputType, CommonColor, FinishResult } from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { ULabel } from "@/ui/ULabel";

class DrawPointLayout {
  private m_model: ModelManager;
  private m_layer: DbLayer;
  private m_auxLayer: MLayer;
  private m_material: THREE.LineBasicMaterial;
  private m_tempObject: THREE.Line | undefined;
  private m_geometry: THREE.BufferGeometry | undefined;
  private points: THREE.Vector3[] = [];
  private m_locationObject: THREE.Line;
  private m_size = { x: 1, y: 1, z: 1 };
  private view: ViewManager;
  private m_auxPoints: {
    orgin: THREE.Vector3;
    dir: THREE.Vector3 | undefined;
  }[] = [];
  private isSubsection: boolean = false;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_dimension: DimensionGroup;
  private m_context: Context;
  private AuxPointGroup: THREE.Group;
  private m_specialParams?: any;
  private protectRadiusProject!: THREE.Line;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_color: number;
  private m_TextLabel: ULabel;
  private m_isShift: Boolean = false;

  constructor(
    context: Context,
    layer: DbLayer,
    color: number,
    specialParams: any
  ) {
    this.m_context = context;
    this.m_model = context.ModelManager;
    this.m_layer = layer;
    this.m_color = color;
    this.m_auxLayer = context.SceneManager.AuxModel;
    this.m_material = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
    });
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.view = context.ViewManager;
    this.m_specialParams = specialParams;
    this.isSubsection = specialParams.isSubsection;
    this.m_dimension = new DimensionGroup(this.view, this.m_auxLayer);
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.AuxPointGroup = new THREE.Group();
    this.m_auxLayer.add(this.AuxPointGroup);
    if (this.m_specialParams.protectRadius.isCreate) {
      this.protectRadiusProject = new ProtectRadius(
        this.m_context,
        this.m_specialParams.protectRadius.radius
      ).ProtectRadiusProject;
      this.protectRadiusProject.visible = false;
      this.m_auxLayer.add(this.protectRadiusProject);
    }
    this.m_context.Dom.style.cursor = "none";
    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
  }

  SetDrawLineForDashed() {}
  onMouseMove(e: PointerEvent): void {}

  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (e.ctrlKey && (e.key == "z"||e.key=="Z")) {
      this.points.pop();
      this.updatedTempModel();
      this.updatedAuxPoint();
      this.m_context.ViewManager.Render();
    }
    if (!this.points.length) return;
    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key == "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let position = this.m_locationObject.position.clone();
      let curDir = position
        .clone()
        .sub(this.points[this.points.length - 1])
        .normalize();

      let angle = curDir.angleTo(new THREE.Vector3(1, 0, 0));

      if (curDir.clone().cross(new THREE.Vector3(1, 0, 0)).z > 0) {
        angle = -angle;
      }
      curDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), -angle);
      curDir.applyAxisAngle(
        new THREE.Vector3(0, 0, 1),
        MathUtils.degToRad(parseFloat(this.m_angleInputValue))
      );

      position = this.points[this.points.length - 1]
        .clone()
        .add(curDir.multiplyScalar(length));
      this.points.push(position);
      this.createdAuxPoint(position);
      this.OnMovePoint({
        point: position,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.updatedTempModel();
      this.view.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }
  private get originPoint() {
    return this.points[this.points.length - 1];
  }
  private get curDir() {
    return this.m_curPoint.clone().sub(this.originPoint).normalize();
  }
  private drawLineAux: THREE.Line | undefined;
  private SignMatrixs: THREE.Matrix4[] = [];
  OnZoom(camera: THREE.Camera) {
    this.m_auxPoints = [];
  }
  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {
    let tolSq = params.tol * params.tol;
    if (this.points.length > 0) {
      let start = new THREE.Vector3();
      let end = new THREE.Vector3();
      let target = new THREE.Vector3();
      let targetOnSegment = new THREE.Vector3();
      if (this.points.length > 1) {
        for (let index = 0; index < this.points.length - 1; index++) {
          start.copy(this.points[index]);
          end.copy(this.points[index + 1]);
          let distance = params.ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          ); // target在射线上，targetOnSegment在线段上
          //  console.log(target.x,target.y,target.z+'          '+targetOnSegment.x,targetOnSegment.y,targetOnSegment.z)
          if (distance <= tolSq) {
            // let location = {x:Number(target.x.toFixed(3)), y:Number(target.y.toFixed(3)), z:Number(target.z.toFixed(3))};
            let location = {
              x: Number(targetOnSegment.x.toFixed(3)),
              y: Number(targetOnSegment.y.toFixed(3)),
              z: Number(targetOnSegment.z.toFixed(3)),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= params.tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let insert = new IndserObject(location, distance, "", []);
            insert.adjust = adjust;
            insert.position = position;
            insert.geo = new THREE.Line3(start.clone(), end.clone());
            params.array.push(insert);
          }
        } // 以上先是用当前移动线检测是否和自己画的线段相交，不包括最后一个线段

        //夹角矫正
        {
          // 这段代码作用为用当前移动线段来和自己画的线的最后一段线检测是否相交
          // alert('yyyyyyyy')
          let direction = new THREE.Vector3();
          this.view.Camera.getWorldDirection(direction);
          let line = new THREE.Line3(
            this.points[this.points.length - 2].clone(),
            this.points[this.points.length - 1].clone()
          );
          let p0 = new THREE.Vector3();
          let p = params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
          let dir0 = line.end.clone().sub(line.start).normalize();
          let dir1 = p0.clone().sub(line.end).normalize();
          let rad = BPMathUtils.angleTo(dir0, dir1, direction);
          let angle = (rad * 180) / Math.PI;
          let orginLength = p0.distanceTo(line.end);
          let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2; // orginLength越小自动偏转幅度越大

          let offsetAngle = (angle + tolAngle) % 90;
          if (offsetAngle <= tolAngle * 2) {
            //五度之内视为垂直
            rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
            let p = dir0
              .applyAxisAngle(direction, rad)
              .multiplyScalar(p0.distanceTo(line.end));

            p = line.end.clone().add(dir0);
            let insert = new IndserObject(
              p,
              Math.sqrt(params.ray.distanceSqToPoint(p)),
              "",
              []
            );
            insert.adjust = p;
            insert.position = IndsertPosition.AuxOrthogonal;
            params.array.push(insert);
          }
        }
      }
      //正交矫正
      {
        // 这段代码作用为 当只有一个点的时候移动线段和 X轴的相交情况
        let direction = new THREE.Vector3();
        this.view.Camera.getWorldDirection(direction);
        let start = this.points[this.points.length - 1].clone();
        let p0 = new THREE.Vector3();
        params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
        let dir0 = new THREE.Vector3(1, 0, 0);
        let dir1 = p0.clone().sub(start).normalize();
        let rad = BPMathUtils.angleTo(dir0, dir1, direction);
        let angle = (rad * 180) / Math.PI;
        let orginLength = p0.distanceTo(start);
        let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2;
        let offsetAngle = (angle + tolAngle) % 90;

        if (offsetAngle <= tolAngle * 2) {
          //五度之内视为垂直
          rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
          let p = dir0
            .applyAxisAngle(direction, rad)
            .multiplyScalar(p0.distanceTo(start));
          p = start.clone().add(dir0);
          let insert = new IndserObject(
            p,
            Math.sqrt(params.ray.distanceSqToPoint(p)),
            "",
            []
          );
          insert.adjust = p;
          insert.position = IndsertPosition.AuxOrthogonal;
          // insert.geo = new THREE.Line3(start.clone(), end.clone());
          params.array.push(insert);
        }
      }
    }
    let ray = new THREE.Ray();

    params.array.forEach((item) => {
      if (item.position == "none") item.position = IndsertPosition.Insert;
      if (item.geo) {
        if (item.geo instanceof THREE.Line3) {
          let line = item.geo as THREE.Line3;
          let p0 = item.adjust ?? item.location;
          let v0 = line.start.clone().sub(line.end.clone());
          let dir0 = v0.clone().normalize();
          if (this.points.length > 0) {
            let point = this.points[this.points.length - 1].clone();
            let v1 = new THREE.Vector3(
              p0.x - point.x,
              p0.y - point.y,
              p0.z - point.z
            );
            let offset = dir0.multiplyScalar(v1.dot(dir0));
            if (offset.lengthSq() <= params.tol * params.tol) {
              //正交矫正
              item.position = IndsertPosition.Vertical;
              item.adjust = new THREE.Vector3(
                p0.x - offset.x,
                p0.y - offset.y,
                p0.z - offset.z
              );
            }
          }
          ray.set(line.start, line.end.clone().sub(line.start).normalize()); // 这里开始检测移动线段是否和场景中两个或两个以上的交点处接触
          params.array.forEach((item1) => {
            if (item1.geo instanceof THREE.Line3) {
              let line1 = item1.geo as THREE.Line3;
              // console.log(params,line,line1,line.start.clone().distanceTo(line1.start.clone()),'!!!!!!!!!');
              if (
                line != line1 &&
                line.start.clone().distanceTo(line1.start.clone()) >
                  0.0000000001 &&
                line.end.clone().distanceTo(line1.end.clone()) >
                  0.00000000001 &&
                line.start.y != line1.end.y
              ) {
                let target = new THREE.Vector3();
                let target1 = new THREE.Vector3();
                let distance = ray.distanceSqToSegment(
                  line1.start.clone(),
                  line1.end.clone(),
                  target
                );
                if (distance < 0.00000000001) {
                  line.closestPointToPoint(target, true, target1); //
                  if (target.distanceTo(target1) < 0.00000001) {
                    // 说明这两条线交叉了，因为这个点同时在两根线上
                    item.position = IndsertPosition.Cross;
                    item.adjust = target;
                    item.distance = target.distanceTo(
                      new THREE.Vector3(
                        item.location.x,
                        item.location.y,
                        item.location.z
                      )
                    );
                  }
                }
              }
            }
          });
        }
      }
    });
  }
  OnMovePoint(params: DrawOperationMovePointParams): void {
    this.m_locationObject.position.copy(params.point);
    this.m_locationObject.visible = true;
    this.m_curPoint.copy(params.point);
    let TextLabelLocation = new THREE.Vector3();
    TextLabelLocation.x = this.m_curPoint.x + this.Scale * 55;
    TextLabelLocation.y = this.m_curPoint.y - this.Scale * 10;
    this.m_TextLabel.SetVaule(
      "按住Ctrl+Z键将撤销回退到上一点,长按Shift锁定正交",
      TextLabelLocation,
      new THREE.Vector3(1, 0, 0)
    );
    if (this.points.length == 0) return;
    this.updatedAuxLine();
    this.updatedDimension();

    if (this.AuxPointGroup.children.length > 0) {
      this.AuxPointGroup.children[0].position.copy(this.m_curPoint);
    }
    this.updatedProtectRadius();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
  }
  OnDownPoint(params: DrawOperationMovePointParams): void {
    let point = params.point;
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      params.position == "none"
    ) {
      point = this.m_curPoint.clone();
    }
    this.points.push(point);
    if (this.points.length == 1) {
      this.createdMoveLineAux();
      this.createdAuxPoint(point, false);
    }
    this.createdAuxPoint(point);
    this.updatedTempModel();
  }
  GetPoints(): Vector3[] {
    return this.points;
  }
  OnWheel(e: WheelEvent): void {
    if (this.points.length) {
      this.m_dimension.Line.SetDimension(
        this.originPoint,
        this.m_curPoint,
        this.Scale
      );
      let xAxis = new THREE.Vector3(1, 0, 0);
      this.m_dimension.Arc.SetDimension(
        this.originPoint,
        xAxis,
        this.curDir,
        this.Scale
      );
    }
  }

  get auxPoint() {
    let ps: THREE.Vector3[] = [];
    this.m_auxPoints.forEach((x) => {
      ps.push(x.orgin);
    });
    return ps;
  }

  Finish(operate: DrawOperateState) {
    this.clear();
    if (operate == DrawOperateState.complete) {
      if (this.points.length >= 1) {
        let key = `${this.m_layer.Id}_${this.m_color}`;
        let lines: DbLine[] = this.m_model.Lines[key]?.lines ?? [];
        let ps: number[] = [];
        let index = 0;
        let indexs: number[] = [];
        let returnLine: DbLine | DBEntity[] = [];
        this.points.forEach((p) => {
          ps.push(p.x, p.y, p.z);
          indexs.push(index);
          if (index != 0 && index != this.points.length - 1) indexs.push(index);
          index++;
        });
        if (this.m_specialParams.isConnectingLine) {
          if (this.isSubsection) {
            for (let i = 0; i < ps.length; i++) {
              if (i % 3 === 0 && i + 3 < ps.length) {
                let line = new DbLine({
                  id: generateUUID(),
                  layerId: this.m_layer.Id,
                  blockId: "",
                  points: new Float32Array([
                    ps[i],
                    ps[i + 1],
                    ps[i + 2],
                    ps[i + 3],
                    ps[i + 4],
                    ps[i + 5],
                  ]),
                  indexes: new Int32Array([0, 1]),
                  color: this.m_color,
                  extend_id: generateUUID(),
                });
                let sphere = new THREE.Sphere();
                let box = new THREE.Box3();
                box.setFromArray(line.points);
                box.getBoundingSphere(sphere);
                line.block_path = generateUUID();
                line.sphere = sphere;
                line.box = box;
                lines.push(line);
                returnLine!.push(line);
              }
            }
          } else {
            let line = new DbLine({
              id: generateUUID(),
              layerId: this.m_layer.Id,
              blockId: "",
              points: new Float32Array(ps),
              indexes: new Int32Array(indexs),
              color: this.m_color,
              extend_id: generateUUID(),
            });
            let sphere = new THREE.Sphere();
            let box = new THREE.Box3();
            box.setFromArray(line.points);
            box.getBoundingSphere(sphere);
            line.block_path = generateUUID();
            line.sphere = sphere;
            line.box = box;
            lines.push(line);
            returnLine!.push(line);
          }
          if (this.m_model.Lines[key]) {
            this.m_model.Lines[key].lines = lines;
            this.view.m_context.ToolsManager.LinesToModel(
              lines,
              this.m_model.Lines[key].model
            );
          } else {
            this.m_model.Lines[key] = {
              canEdit: true,
              lines: lines,
              model: this.m_model.addLinesToModel(
                this.m_layer.Id,
                this.m_color,
                lines
              ),
            };
          }
        }
        return {
          dblines: returnLine,
          matrixs: this.SignMatrixs,
        };
      }
    } else if (operate == DrawOperateState.cancel) {
      return [];
    }
    return [];
  }
  private get Scale() {
    return this.view.ScreenModelScale;
  }

  private updatedTempModel() {
    this.updatedTempLine();
    this.updatedAuxLine();
    this.updatedDimension();
    this.updatedProtectRadius();
  }
  private updatedTempLine() {
    if (this.m_specialParams.isConnectingLine) {
      if (this.m_geometry) {
        this.m_geometry.setFromPoints(this.points);
        this.m_tempObject!.computeLineDistances();
      } else {
        this.m_geometry = new THREE.BufferGeometry();
        this.m_geometry.setFromPoints(this.points);
        this.m_tempObject = new THREE.Line(this.m_geometry, this.m_material);
        this.m_tempObject.computeLineDistances();
        this.m_tempObject.frustumCulled = false;
        this.m_auxLayer.add(this.m_tempObject);
      }
      if (!this.points.length) {
        this.drawLineAux!.geometry.setFromPoints([]);
        this.drawLineAux!.computeLineDistances();
      }
    }
  }
  private updatedAuxLine() {
    if (this.drawLineAux) {
      let arr: THREE.Vector3[] = [];
      if (this.points.length) {
        arr = [this.points[this.points.length - 1], this.m_curPoint];
        if (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) {
          this.m_curPoint = this.m_context.ToolsManager.GetOrthogonalityPoint(
            this.m_curPoint,
            this.points[this.points.length - 1]
          );
          arr = [this.points[this.points.length - 1], this.m_curPoint];
        }
      }
      this.drawLineAux.geometry.setFromPoints(arr);

      this.drawLineAux.computeLineDistances();
    }
  }
  private updatedDimension() {
    let xAxis = new THREE.Vector3(1, 0, 0);
    if (this.points.length) {
      this.m_dimension.Line.setVisible(true);
      this.m_dimension.Arc.setVisible(true);
      this.m_dimension.Line.SetDimension(
        this.originPoint,
        this.m_curPoint,
        this.Scale
      );
      this.m_dimension.Arc.SetDimension(
        this.points[this.points.length - 1],
        xAxis,
        this.curDir,
        this.Scale
      );
      let angle = this.curDir.clone().angleTo(xAxis);
      if (this.curDir.clone().cross(xAxis).z > 0) {
        angle = Math.PI * 2 - angle;
      }
      angle = (angle / Math.PI) * 180;
      let distance = (this.m_curPoint.distanceTo(this.originPoint) * 1000)
        .toFixed(2)
        .toString();
      this.m_dimension.Line.SetVaule(distance);
      this.m_distanceInputValue = distance;
      this.m_dimension.Arc.SetVaule(angle.toFixed(0).toString());
      this.m_angleInputValue = angle.toFixed(0).toString();
      this.m_dimension.Anchor.SetPoint(this.auxPoint, this.Scale);
    } else {
      this.m_dimension.Line.setVisible(false);
      this.m_dimension.Arc.setVisible(false);
    }
  }

  private updatedProtectRadius(p: THREE.Vector3 | undefined = this.m_curPoint) {
    if (this.protectRadiusProject) {
      this.protectRadiusProject.visible = true;
      if (this.points.length) {
        this.protectRadiusProject.position.copy(p);
      } else {
        this.protectRadiusProject.visible = false;
      }
    }
  }

  private updatedAuxPoint() {
    if (this.points.length) {
      let lastAuxPoint =
        this.AuxPointGroup.children[this.AuxPointGroup.children.length - 1];
      this.m_context.ToolsManager.ObjectsClear([lastAuxPoint]);
    } else {
      this.m_context.ToolsManager.GroupClear(this.AuxPointGroup);
      this.AuxPointGroup = new THREE.Group();
      this.m_auxLayer.add(this.AuxPointGroup);
    }
    this.SignMatrixs.pop();
  }
  // 辅助线
  private createdMoveLineAux() {
    this.drawLineAux = this.m_model.m_context.ToolsManager.CreatedAuxLine();
  }
  // 当前辅助标志和x轴的夹角度数
  private includedAngle: number = 0;
  // 辅助标志
  private createdAuxPoint(position: THREE.Vector3, isSave: boolean = true) {
    if (this.points.length > 1) {
      let dir = this.points[this.points.length - 1]
        .clone()
        .sub(this.points[this.points.length - 2])
        .normalize();
      let xAxis = new THREE.Vector3(1, 0, 0);
      this.includedAngle = dir.angleTo(xAxis);
      if (dir.clone().cross(xAxis).z > 0) {
        this.includedAngle = Math.PI * 2 - this.includedAngle;
      }
    }
    let geo = new THREE.BufferGeometry();
    let positions = new Float32Array(37 * 3);
    for (let index = 0; index <= 36; index++) {
      let angle = (Math.PI * 2 * index) / 36;
      let x = Math.cos(angle) * 0.2;
      let y = Math.sin(angle) * 0.2;
      let z = 0;
      positions[index * 3] = x;
      positions[index * 3 + 1] = y;
      positions[index * 3 + 2] = z;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let AuxPoint = new THREE.Line(geo, material);
    AuxPoint.position.copy(position);
    this.AuxPointGroup.add(AuxPoint);
    let matrix = new THREE.Matrix4();
    matrix.setPosition(position);
    let scale = new THREE.Vector3(1, 1, 1);
    if (!(this.m_specialParams.scale instanceof Object)) {
      scale.x = this.m_specialParams.scale;
      scale.y = this.m_specialParams.scale;
      scale.z = this.m_specialParams.scale;
    } else {
      scale.x = this.m_specialParams.scale.x;
      scale.y = this.m_specialParams.scale.y;
      scale.z = this.m_specialParams.scale.z;
    }
    matrix.scale(scale);
    if (isSave) {
      this.SignMatrixs.push(matrix);
    }
    if (this.points.length >= 2) {
      let len = this.SignMatrixs.length;
      let pos1 = new THREE.Vector3();
      let scale1 = new THREE.Vector3();
      let quat1 = new THREE.Quaternion();
      quat1.setFromAxisAngle(new THREE.Vector3(0, 0, 1), this.includedAngle);
      this.SignMatrixs[len - 1].decompose(pos1, new THREE.Quaternion(), scale1);
      let pos2 = new THREE.Vector3();
      let scale2 = new THREE.Vector3();
      let quat2 = new THREE.Quaternion();
      quat2.setFromAxisAngle(new THREE.Vector3(0, 0, 1), this.includedAngle);
      this.SignMatrixs[len - 2].decompose(pos2, new THREE.Quaternion(), scale2);
      this.SignMatrixs[len - 1].compose(pos1, quat1, scale1);
      this.SignMatrixs[len - 2].compose(pos2, quat2, scale2);
    }
  }
  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempObject!,
      this.drawLineAux!,
      this.protectRadiusProject!,
    ]);
    this.m_dimension?.dispose();
    this.m_TextLabel?.dispose();
    this.m_context.ToolsManager.GroupClear(this.AuxPointGroup);
  }
}

export { DrawPointLayout };
